import { doodBlockConfig, propertyValue } from "./dood-block-config";
import { cartPagePresetBlockBackgroundColorProperty } from "./cart-page-preset-block-background.util";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";
import type { LoyaltyPreviewOfferedProduct } from "./preview-scenarios/loyalty-preview.shared";

/** Reward row shown by the Como asset list in CB2 preview. */
export type ComoAssetListPreviewAsset = {
  key: string;
  name: string;
  description?: string;
  image?: string;
  redeemable: boolean;
  status: string;
  is_usable: string;
  points_needed?: number;
  /** Remaining points until unlock, in hundredths (same unit as live Como API). */
  points_remaining?: number;
  offeredProducts?: readonly LoyaltyPreviewOfferedProduct[];
};

/** Serializable preview VM for `ComoAssetListBlockComponent`. */
export type ComoAssetListBlockScenarioContent = {
  comoUser: {
    points_balance: {
      used_by_payment: boolean;
      monetary: number;
      non_monetary: number;
    };
    credit_balance: {
      used_by_payment: boolean;
      monetary: number;
      non_monetary: number;
    };
    assets: readonly ComoAssetListPreviewAsset[];
  } | null;
};

export const COMOASSETLISTBLOCK_LEGACY_FIELD_ORDER = [
  "heading",
  "subheading",
  "display_credits",
  "display_points",
  "display_redeemable_gifts",
  "display_not_redeemable_gifts",
  "display_redeemable_point_shop",
  "display_not_redeemable_point_shop",
  "display_not_usable_point_shop",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  comoAssetListBlockDescriptor: _generatedComoAssetListBlockDescriptor,
  comoAssetListBlockBlocDefinition,
  comoAssetListBlockDashboardLayout,
  comoAssetListBlockConfig,
  comoAssetListBlockResolveConfig,
  comoAssetListBlockProperties,
  comoAssetListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Como - Asset list",
    componentType: "ComoAssetListBlockComponent",
    category: "como",
    description: "Como - Asset list.",
    keywords: [],
    allowedIn: [
      { bloc: "CartPageComponent", slot: "*" },
      { bloc: "KioskCartPageComponent", slot: "*" },
    ],

    sharedFields: {},
  },

  () => ({
    background_color: cartPagePresetBlockBackgroundColorProperty(),
    heading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.como-asset-list.heading.label",
      },
    }),
    subheading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.como-asset-list.subheading.label",
      },
    }),
    display_credits: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.como-asset-list.display-credits.label",
      },
    }),
    display_points: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.como-asset-list.display-points.label",
      },
    }),
    display_redeemable_gifts: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.como-asset-list.display-redeemable-gifts.label",
      },
    }),
    display_not_redeemable_gifts: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.como-asset-list.display-not-redeemable-gifts.label",
      },
    }),
    display_redeemable_point_shop: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.como-asset-list.display-redeemable-point-shop.label",
      },
    }),
    display_not_redeemable_point_shop: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.como-asset-list.display-not-redeemable-point-shop.label",
      },
    }),
    display_not_usable_point_shop: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.como-asset-list.display-not-usable-point-shop.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.como-asset-list.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-page.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend: "pages.blocks.como-asset-list.fieldset.titles.label",
            fields: ["heading", "subheading"],
          },
          {
            legend: "pages.blocks.como-asset-list.fieldset.balances.label",
            fields: ["display_credits", "display_points"],
          },
          {
            legend: "pages.blocks.como-asset-list.fieldset.gifts.label",
            fields: [
              "display_redeemable_gifts",
              "display_not_redeemable_gifts",
            ],
          },
          {
            legend: "pages.blocks.como-asset-list.fieldset.point-shop.label",
            fields: [
              "display_redeemable_point_shop",
              "display_not_redeemable_point_shop",
              "display_not_usable_point_shop",
            ],
          },
        ],
      },
    ],
  }),
);

export function comoAssetListBlockDescriptor(): IBlockDescriptor;
export function comoAssetListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function comoAssetListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedComoAssetListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedComoAssetListBlockDescriptor,
    comoAssetListBlockProperties,
    fieldOrder,
  );
}

export {
  comoAssetListBlockBlocDefinition,
  comoAssetListBlockDashboardLayout,
  comoAssetListBlockProperties,
  comoAssetListBlockConfig,
  comoAssetListBlockResolveConfig,
  comoAssetListBlockDefinitionSetupScenarios,
};

export type ComoAssetListBlockConfig = typeof comoAssetListBlockConfig;
